import type {HeaderBiddingConfig} from "header-bidding-client-base/modules/config/domain/headerBiddingConfig";
import type {GlobalConfig} from "header-bidding-client-base/modules/config/domain/globalConfig";
import {HeaderBiddingService} from "header-bidding-client-base/modules/header-bidding/domain/headerBiddingService";
import type {PrebidJsBaseBiddersLoader} from "../prebid-js/bidders/prebidJsBaseBiddersLoader";
import {HeaderBiddingAdUnit} from "../../../config/domain/headerBiddingConfig";
import {ILogger} from "ads-client-base/modules/logger/domain/iLogger";
import {MeteoredCMPAdapter} from "ads-client-base/modules/cmp/infrastructure/meteoredCMPAdapter";
import {HeaderBiddingPlatforms} from "header-bidding-base/modules/header-bidding/domain/platforms";

declare global {
    interface Window {
        googletag?: any;
        alpred?: {
            hb?: any
        };
    }
}

export class GptHeaderBiddingAdapter {
    private headerBiddingService: HeaderBiddingService;
    private _adServerLoadResumed: boolean;
    private _displayInitiated: boolean;

    public constructor(
        private config: HeaderBiddingConfig,
        globalConfig: GlobalConfig,
        private logger: ILogger,
        prebidJsBiddersLoader?: PrebidJsBaseBiddersLoader
    ) {
        this.headerBiddingService = new HeaderBiddingService(config, globalConfig, logger, prebidJsBiddersLoader);
        this._adServerLoadResumed = false;
        this._displayInitiated = false;
    }

    public async init(initiatedCallback?: () => void) {

        await this.headerBiddingService.init();

        this.initWindowInterface();

        this.initEventListeners();

        MeteoredCMPAdapter.initCMPListener(() => {
            this.startCMDQueue();
            if (initiatedCallback) {
                initiatedCallback();
            }
        });

    }

    public refreshSlotsById(slotIds?: string[] | string): void {
        this.startFailsafe();

        window.googletag.cmd.push(async () => {

            const gptSlots: any[] = window.googletag.pubads().getSlots();

            slotIds = slotIds ? Array.isArray(slotIds) ? slotIds : [slotIds] : [];
            const videoSlots: HeaderBiddingAdUnit[] = [];
            const slots: any[] = slotIds.length > 0 ? slotIds.map((slotId) => {
                const slot = gptSlots.find((slot: any) => slot.getSlotElementId() === slotId);
                if (!slot) {
                    // Check if is slot video
                    const videoSlot = this.config.adUnits.find((adUnit) => adUnit.type.video && adUnit.divId == slotId);
                    if (videoSlot) {
                        if (!videoSlot.adUnitPath || !videoSlot.divId) {
                            this.logger.error("Video slot adUnitPath and divId are required", videoSlot);
                            return;
                        }
                        videoSlots.push(videoSlot);
                        return;
                    }

                    this.logger.warn("Slot with id is not defined in GPT", slotId);
                    return;
                }

                return slot;
            }).filter((slot) => !!slot) : gptSlots;

            const slotsNotExists = slots.filter((slot) => !this.headerBiddingService.getAdUnitPath(slot.getSlotElementId()));
            if (slotsNotExists.length > 0) {

                const slotsParsed: {divId: string, adUnitPath: string, sizes: number[][]}[] = slotsNotExists.map((slot: any) => {
                    let adUnitPath = slot.getAdUnitPath();
                    if (adUnitPath.startsWith('/')) {
                        adUnitPath = adUnitPath.slice(1);
                    }
                    let sizes = slot.getSizes();
                    sizes = sizes.length > 0 ? sizes.filter((size: any) => typeof size === 'object' && size.getWidth && size.getHeight) : [];
                    return {
                        divId: slot.getSlotElementId(),
                        adUnitPath: adUnitPath,
                        sizes: sizes.map((size: any) => [size.getWidth(), size.getHeight()])
                    }
                });

                await this.headerBiddingService.addAdUnits(slotsParsed);
            }

            const videoSlotsNotExists = videoSlots.filter((slot) => !this.headerBiddingService.getAdUnitPath(slot.adUnitPath!));
            if (videoSlotsNotExists.length > 0) {
                const slotsParsed: {divId: string, adUnitPath: string}[] = videoSlotsNotExists.map((slot) => {
                    return {
                        divId: slot.divId!,
                        adUnitPath: slot.adUnitPath!,
                    }
                });

                await this.headerBiddingService.addAdUnits(slotsParsed);
            }

            let filteredSlotIds = slotIds;
            let filteredSlots = slots;
            let filteredVideoSlots = videoSlots;
            if (slotIds.length) {
                filteredSlotIds = this.headerBiddingService.filterAddedAdUnits(slotIds);
                filteredSlots = slots.filter((slot) => filteredSlotIds.includes(slot.getSlotElementId()));
                filteredVideoSlots = videoSlots.filter((slot) => filteredSlotIds.includes(slot.divId!));

                const slotsNotAdded = slots.filter((slot) => !filteredSlotIds.includes(slot.getSlotElementId()));
                if (slotsNotAdded.length > 0) {
                    this.logger.log("Displaying slots without header bidding", slotsNotAdded.map((slot) => slot.getSlotElementId()));
                    this.refreshGamSlots(slotsNotAdded, false);
                }

                if (!filteredSlots.length && !filteredVideoSlots.length) {
                    this._adServerLoadResumed = true;
                    return;
                }
            }

            this.headerBiddingService.requestBids((err, bidsData) => {
                if (err) {
                    this.logger.error("Error requesting bids", err);
                }
                this.refreshGamSlots(filteredSlots, true);

                // Send video instream bids data to client
                if (bidsData && bidsData.videoInstreamBidsData && bidsData.videoInstreamBidsData.length) {
                    bidsData.videoInstreamBidsData.forEach((bidData) => {
                        const event = new CustomEvent('alpred:hb:video_instream_bid_received', {
                            bubbles: true,
                            detail: bidData,
                        });
                        document.dispatchEvent(event);
                    });
                }
            }, filteredSlotIds);

        });

    }

    public refreshSlots(slots?: any[] | any): void {
        slots = slots ? Array.isArray(slots) ? slots : [slots] : undefined;
        return this.refreshSlotsById(slots?.map((slot: any) => slot.getSlotElementId()));
    }

    public destroySlotsById(slotIds?: string[] | string): void {

        window.googletag.cmd.push(() => {

            const gptSlots: any[] = window.googletag.pubads().getSlots();

            slotIds = slotIds ? Array.isArray(slotIds) ? slotIds : [slotIds] : [];
            const slots: any[] = slotIds.length > 0 ? slotIds.map((slotId) => {
                const slot = gptSlots.find((slot: any) => slot.getSlotElementId() === slotId);
                if (!slot) {
                    this.logger.warn("Slot with id is not defined in GPT", slotId);
                    return;
                }

                return slot;
            }).filter((slot) => !!slot) : gptSlots;

            this.headerBiddingService.removeAdUnits(slots.map((slot: any) => slot.getSlotElementId())).then(() => {
                window.googletag.destroySlots(slots);
            });

        });
    }

    public destroySlots(slots?: any[] | any): void {
        slots = slots ? Array.isArray(slots) ? slots : [slots] : undefined;
        return this.destroySlotsById(slots?.map((slot: any) => slot.getSlotElementId()));
    }

    public disableBidder(bidder: string, slotIds: string[]): void {
        return this.headerBiddingService.disableBidder(bidder, slotIds);
    }

    public enableBidder(bidder: string, slotIds: string[]): void {
        return this.headerBiddingService.enableBidder(bidder, slotIds);
    }

    public disableAdUnits(adUnits: string | string[]): void {
        return this.headerBiddingService.disableAdUnits(Array.isArray(adUnits) ? adUnits : [adUnits]);
    }

    public enableAdUnits(adUnit: string | string[]): void {
        return this.headerBiddingService.enableAdUnits(Array.isArray(adUnit) ? adUnit : [adUnit]);
    }

    private refreshGamSlots(slots: any, resume: boolean): void {
        if (resume && !this._adServerLoadResumed) this._adServerLoadResumed = true;
        window.googletag.cmd.push(function () {
            window.googletag.pubads().refresh(slots);
        });
    }

    private initWindowInterface(): void {

        window.alpred = window.alpred || {};
        window.alpred.hb = window.alpred.hb || {};
        window.alpred.hb = {
            ...window.alpred.hb,
            refresh: this.refreshSlots.bind(this),
            refreshById: this.refreshSlotsById.bind(this),
            destroySlots: this.destroySlots.bind(this),
            destroySlotsById: this.destroySlotsById.bind(this),
            disableBidder: this.disableBidder.bind(this),
            enableBidder: this.enableBidder.bind(this),
            disableAdUnits: this.disableAdUnits.bind(this),
            enableAdUnits: this.enableAdUnits.bind(this),
        };

    }

    private initEventListeners(): void {

        // Emit slotRenderEnded event with real ad sizes
        window.googletag.cmd.push(() => {
            window.googletag.pubads().addEventListener('slotRenderEnded', async (event: any) => {
                let sizes: [number, number] | undefined;
                let platform: string | undefined;
                const amzniid = event.slot.getTargeting('amzniid') && event.slot.getTargeting('amzniid').length ? event.slot.getTargeting('amzniid')[0] : undefined;
                const hbAdid = event.slot.getTargeting('hb_adid') && event.slot.getTargeting('hb_adid').length ? event.slot.getTargeting('hb_adid')[0] : undefined;

                // Amazon TAM
                if (amzniid) {
                    sizes = this.headerBiddingService.getAdSizes(amzniid, HeaderBiddingPlatforms.AMAZON_TAM);
                    platform = HeaderBiddingPlatforms.AMAZON_TAM;
                }
                // Prebid
                if (!sizes && hbAdid) {
                    // todo improve detection to remove setTimeout
                    await new Promise(resolve => setTimeout(resolve, 100));
                    sizes = this.headerBiddingService.getAdSizes(hbAdid, HeaderBiddingPlatforms.PREBID);
                    platform = HeaderBiddingPlatforms.PREBID;
                }

                if (!sizes) {
                    sizes = event.size;
                    platform = 'GPT';
                }

                this.logger.log("slotRenderEnded", {
                    slot: event.slot.getSlotElementId(),
                    size: sizes,
                    isEmpty: event.isEmpty,
                    platform: platform,
                    amzniid: amzniid,
                    hbAdid: hbAdid,
                });
                var customEvent = new CustomEvent('alpred:hb:slotRenderEnded', {
                    bubbles: true,
                    detail: {
                        slot: event.slot,
                        size: sizes,
                        isEmpty: event.isEmpty,
                        platform: platform,
                    }
                });
                document.dispatchEvent(customEvent);
            });
        });
    }

    private startFailsafe(): void {
        if (!this._displayInitiated) {
            this._displayInitiated = true;
            setTimeout(() => {
                if (!this._adServerLoadResumed) {
                    console.error("Ad server load not resumed after failsafe timeout, resuming ad server load.");
                    var event = new CustomEvent('alpred:hb:load_failed', {
                        bubbles: true
                    });
                    document.dispatchEvent(event);
                }
            }, this.config.failsafeTimeout);
        }
    }

    private startCMDQueue() {
        if (window.alpred) {
            // Run commands in queue
            if (window.alpred.hb.cmd && window.alpred.hb.cmd.length > 0) {
                while (window.alpred.hb.cmd.length > 0) {
                    try {
                        window.alpred.hb.cmd.shift()();
                    } catch (err: any) {
                        this.logger.error(err);
                    }
                }
            }

            window.alpred.hb.cmd = {
                push: function (callback: any) {
                    callback.call();
                }
            };
        }
    }

}
